import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { kMillisecondsPerMinute } from '../../utility/constants.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameList, SerializationContext, SerializedData } from '../dto/dto.js';
import { ServiceDuration } from './service-duration.js';

export class FlexibleServiceDuration extends DataModel {
  static readonly TYPE_ID: string = '1cf8b38d7db8406fa2870cf1d52480a7';

  public readonly fractions: ServiceDuration[];
  public readonly totalDuration: Duration;

  constructor(
      fractions: ServiceDuration[],
  ) {
    super();

    this.fractions = fractions;
    this.totalDuration = FlexibleServiceDuration.calculateTotalDuration(fractions);
  }

  get $typeId(): string {
    return FlexibleServiceDuration.TYPE_ID;
  }

  static empty(): FlexibleServiceDuration {
    return new FlexibleServiceDuration(
        [new ServiceDuration(dayjs.duration(0), true)],
    );
  }

  static fromDuration(duration: Duration, blocking = true): FlexibleServiceDuration {
    return new FlexibleServiceDuration(
        [new ServiceDuration(duration, blocking)],
    );
  }

  static fromMinutes(minutes: number, blocking = true): FlexibleServiceDuration {
    return new FlexibleServiceDuration(
        [new ServiceDuration(dayjs.duration(minutes * kMillisecondsPerMinute), blocking)],
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): FlexibleServiceDuration {
    return new FlexibleServiceDuration(
        MapUtils.getList<ServiceDuration>(
            map,
            'fractions',
            (value) => context.fromData<ServiceDuration>(value as SerializedData),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'fractions': this.fractions.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new FlexibleServiceDuration(
        this.fractions.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: FlexibleServiceDuration): boolean {
    if(this === other) {
      return true;
    }

    return isSameList<ServiceDuration>(other.fractions, this.fractions, (a, b) => a.isSame(b));
  }

  shrink(amount: Duration): FlexibleServiceDuration {
    const fractions: ServiceDuration[] = [];

    for(let i = 0; i < this.fractions.length; i++) {
      if(i < this.fractions.length - 1) {
        fractions.push(this.fractions[i].clone());
      }
      else {
        const milliseconds = this.fractions[i].duration.asMilliseconds() - amount.asMilliseconds();
        if(milliseconds > 0) {
          fractions.push(
              new ServiceDuration(
                  dayjs.duration(milliseconds),
                  this.fractions[i].blocking,
              ),
          );
        }
      }
    }

    return new FlexibleServiceDuration(fractions);
  }

  isBlockingAtMinute(minutes: number): boolean {
    const milliseconds = minutes * kMillisecondsPerMinute;
    let startTime = 0;

    for(let i = 0; i < this.fractions.length; i++) {
      const duration = this.fractions[i].duration.asMilliseconds();
      if(milliseconds >= startTime && milliseconds < startTime + duration) {
        if(this.fractions[i].blocking) {
          return true;
        }
      }

      startTime += duration;
    }

    return false;
  }

  static calculateTotalDuration(fractions: ServiceDuration[]): Duration {
    let milliseconds = 0;
    fractions.forEach((e) => milliseconds += e.duration.asMilliseconds());

    return dayjs.duration(milliseconds);
  }
}

export class FlexibleServiceDurationBuilder extends DataModelBuilder<FlexibleServiceDuration> {
  build(context: SerializationContext, map: SerializedData): FlexibleServiceDuration {
    return FlexibleServiceDuration.fromMap(context, map);
  }
}
